<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { Button, Card, Table, SearchInput } from 'ui-kit'
import { Plus } from 'lucide-vue-next'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import { useEnvironment } from '@/composables/useEnvironment'
import { useSchools } from '@/composables/api/useSchoolsApi'
import type { ServiceType } from '@/constants/environments'

const router = useRouter()
const { currentService } = useEnvironment()

// Re-query whenever the selected service changes (the queryKey includes it).
const { data, error } = useSchools(currentService.value.key as ServiceType)

// Present the raw API rows for the table: the ui-kit Table's `status` column
// type calls `.toLowerCase()` on the cell value, so boolean/undefined fields
// (is_production, use_sections) crash it. Render them as plain "Yes"/"No" text
// and format the date.
const schools = computed(() =>
  (data.value ?? []).map((s: any) => ({
    ...s,
    full_name: s.full_name ?? '—',
    production: s.is_production ? 'Yes' : 'No',
    created: s.created_at ? new Date(s.created_at).toLocaleDateString() : '—',
  })),
)

const columns = [
  { key: 'name', label: 'Name' },
  { key: 'full_name', label: 'Full Name' },
  { key: 'production', label: 'Production' },
  { key: 'created', label: 'Created' },
]

function handleCreate() {
  router.push({ name: 'schools-create' })
}

function handleEdit(row: Record<string, any>) {
  router.push({ name: 'schools-detail', params: { id: row.id } })
}
</script>

<template>
  <AdminLayout>
    <div class="school-list">
      <div class="school-list__toolbar">
        <h2 class="school-list__heading">
          Schools
          <span class="school-list__service-label">{{ currentService.label }}</span>
        </h2>
        <div class="school-list__actions">
          <SearchInput placeholder="Search schools..." />
          <Button @click="handleCreate">
            <Plus :size="16" />
            Create School
          </Button>
        </div>
      </div>

      <Card>
        <Table
          :items="schools"
          :columns="columns"
          empty-text="No schools found."
          :show-edit="true"
          @edit="handleEdit"
        />
      </Card>

      <div v-if="error" class="school-list__error">
        Failed to load schools: {{ error }}
      </div>
    </div>
  </AdminLayout>
</template>

<style scoped>
.school-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.school-list__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.school-list__heading {
  font-size: 20px;
  font-weight: 600;
  color: #1c1917;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.school-list__service-label {
  font-size: 12px;
  font-weight: 500;
  color: #78716c;
  background: #f5f5f4;
  padding: 2px 8px;
  border-radius: 4px;
}

.school-list__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.school-list__error {
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
}
</style>
